/**
 * TanStack Query client configured at the shell (R-18.106 server-state tool). Defaults
 * follow the chapter: staleTime 5 min (R-18.109), gcTime 10 min (R-18.111), queries
 * retry up to 3× with exponential backoff and mutations do not retry (R-18.110).
 *
 * Auth failures (401/403) are NOT retried here: the axios client (`shared/api/client.ts`)
 * already runs the single-flight 401->refresh rotation and redirects on failure, so a
 * query-level retry on those statuses would re-amplify refresh attempts and fight the
 * interceptor. We bail out of retry for 401/403 and only retry transient failures.
 *
 * A global query-error handler surfaces failures via AntD `message` (R-18.65) so no
 * async error fails silently. The headline is always a translated, user-facing string —
 * raw backend error text (un-i18n'd) is never shown as the headline. Under it the toast
 * carries the TECHNICAL line the backend supplied — the error code and the correlation id
 * (`shared/api/error.ts`) — because "Connection error. Please try again." on its own gives
 * neither the user nor support anything to act on. That id is the lookup key for Admin →
 * Errors, so a screenshotted toast is enough to find the exact server-side failure.
 *
 * Per-query opt-out: a query/mutation that renders its OWN inline/graceful error state
 * (e.g. an inline `<Alert>` with retry) sets `meta: { suppressGlobalError: true }` so it
 * does not ALSO fire the global toast — one error message per failure, no conflicting
 * text. Queries with no local handling keep the global toast (the default).
 *
 * EVERY successful mutation invalidates the WHOLE query cache (the MutationCache
 * `onSuccess` below). With a 5-minute staleTime, any query key a mutation forgot to
 * invalidate by hand kept showing pre-mutation data until a reload — the vehicle card
 * still named the old owner after a purchase registered the transfer, and the same hole
 * existed for every cross-entity write (a mutation cannot know every read that renders
 * its side effects). Invalidation is cheap: it marks queries stale and refetches only the
 * ones currently on screen, so the cost of a mutation is a handful of list/detail GETs —
 * the price of never showing stale data after a write. Targeted `invalidateQueries` calls
 * in mutation hooks remain harmless (and new hooks no longer need them); a genuinely
 * chatty mutation can opt out with `meta: { skipGlobalInvalidation: true }`.
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { message } from 'antd';
import { isAxiosError } from 'axios';
import { createElement, Fragment } from 'react';

import { describeApiError, technicalSummary } from '@/shared/api/error';
import { i18n } from '@/shared/lib/i18n';

// Augment the shared `meta` bag so `suppressGlobalError` is typed at every call site
// (TanStack's `Register` seam) — a query/mutation that handles its own error sets it.
declare module '@tanstack/react-query' {
  interface Register {
    // `skipGlobalInvalidation` on a QUERY exempts it from the post-mutation invalidate-all
    // below — for reads whose ANSWER no mutation can change (e.g. the signed-URL mint for an
    // immutable media object: a refetch only rotates the token, which forces every visible
    // <img> to re-download identical bytes; on 21.08.2026 that turned each action on a
    // photo-bearing card into megabytes of image re-downloads).
    queryMeta: { suppressGlobalError?: boolean; skipGlobalInvalidation?: boolean };
    mutationMeta: { suppressGlobalError?: boolean; skipGlobalInvalidation?: boolean };
  }
}

const MAX_QUERY_RETRIES = 3;

function httpStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = httpStatus(error);
  // Never retry auth failures — the interceptor owns the refresh/redirect path.
  // No other 4xx is retried either: a client error is deterministic (the same request
  // gets the same answer), and the retry window only delayed the error state — e.g. a
  // picker kept showing the PREVIOUS result list for seconds while a 400 was retried.
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < MAX_QUERY_RETRIES;
}

/**
 * The translated headline for a failure. Raw backend English is never the headline
 * (R-18.65) — it is attached as the technical line below (see notifyError).
 */
function headlineKey(status: number | undefined): string {
  if (status === 401 || status === 403) return 'error.unauthorized';
  if (status === 404) return 'error.notFound';
  // Any other 4xx is the server REFUSING a well-formed request — a rule said no. Calling that a
  // connection error told the operator to check their network over a decision that has nothing to
  // do with it, and hid the one line that does explain it: the code on the technical line below.
  if (status !== undefined && status >= 400 && status < 500) return 'error.refused';
  return 'error.connection';
}

function notifyError(error: unknown): void {
  const info = describeApiError(error);
  const headline = i18n.t(headlineKey(info.status));
  const technical = technicalSummary(info);

  // The headline stays its own text node so it reads as one sentence; the technical line —
  // the backend's error code and the requestId — goes underneath in small type. That second
  // line is what a user reads back to support, and what an admin pastes into the Admin →
  // Errors filter to pull up the full server-side cause. A failure that carried neither (a
  // network drop) shows the headline alone rather than an empty second line.
  if (technical === '') {
    void message.error(headline);
    return;
  }
  void message.error({
    content: createElement(
      Fragment,
      null,
      createElement('span', null, headline),
      createElement(
        'div',
        { style: { fontSize: 12, opacity: 0.65, marginTop: 2 } },
        technical,
      ),
    ),
  });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: shouldRetryQuery,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Skip the global toast when the query owns its inline/graceful error state.
      if (query.meta?.suppressGlobalError === true) return;
      notifyError(error);
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      // A write happened somewhere — every cached read may now be lying. Mark it all stale;
      // only the queries actually on screen refetch. Runs before the mutation's own
      // onSuccess and is deliberately not awaited, so per-hook handlers are not delayed.
      // Queries whose answer no mutation can change opt out via their own
      // `skipGlobalInvalidation` meta (see the Register augmentation above).
      if (mutation.meta?.skipGlobalInvalidation === true) return;
      void queryClient.invalidateQueries({
        predicate: (query) => query.meta?.skipGlobalInvalidation !== true,
      });
    },
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.suppressGlobalError === true) return;
      notifyError(error);
    },
  }),
});
